import { useState, useEffect, useCallback, useMemo } from 'react'
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer
} from 'recharts'
import { getProductPerformanceData } from '../../supabase-client'
import { DashboardTabs } from '@/components/ui/dashboard-tabs'
import DateRangeSelector from './DateRangeSelector'

import { STRAIN_COLORS } from '../sales/timeline/chart-config'

// Use the shared chart palette — colours assigned by sort-order index
const chartPalette = STRAIN_COLORS.default


const metricTabs = [
  { value: 'value', label: 'Revenue (£)' },
  { value: 'mass', label: 'Mass (kg)' }
]

// Custom tooltip showing both value and mass for each strain
const CustomTooltip = ({ active, payload, label, massLookup }) => {
  if (active && payload && payload.length) {
    const sortedPayload = [...payload].sort((a, b) => a.value - b.value)
    const weekMass = massLookup?.[label]

    return (
      <div className="bg-white border border-border rounded-lg p-3 shadow-md max-w-[260px]">
        <p className="text-sm font-medium mb-2">{label}</p>
        {sortedPayload.map((entry, index) => {
          if (entry.dataKey === 'total') return null
          const mass = weekMass?.[entry.dataKey]
          return (
            <div key={index} className="flex items-center justify-between gap-4 text-xs">
              <div className="flex items-center gap-2">
                <div
                  className="w-3 h-3 rounded-sm"
                  style={{ backgroundColor: entry.color }}
                />
                <span className="text-muted-foreground truncate">
                  {entry.dataKey}
                </span>
              </div>
              <div className="flex gap-3 font-medium">
                <span>£{entry.payload?.[`_value_${entry.dataKey}`]?.toFixed(2) ?? entry.value?.toFixed(2) ?? '0.00'}</span>
                {mass != null && (
                  <span className="text-muted-foreground">{mass.toFixed(1)} kg</span>
                )}
              </div>
            </div>
          )
        })}
        <div className="flex items-center justify-between gap-4 text-xs mt-2 pt-2 border-t">
          <span className="text-muted-foreground font-medium">Total</span>
          <div className="flex gap-3 font-semibold">
            <span>£{payload[0]?.payload?._valueTotal?.toFixed(2) ?? '0.00'}</span>
            <span className="text-muted-foreground">{payload[0]?.payload?._massTotal?.toFixed(1) ?? '0'} kg</span>
          </div>
        </div>
      </div>
    )
  }
  return null
}

export default function ProductPerformanceChart({ dateRange: externalDateRange, loading: externalLoading, compact = false }) {
  const [internalDateRange, setInternalDateRange] = useState('ytd')
  const [rawData, setRawData] = useState({ value: [], mass: [] })
  const [loading, setLoading] = useState(true)
  const [metric, setMetric] = useState('value')

  // Use external date range if provided, otherwise use internal
  const dateRange = externalDateRange || internalDateRange

  const loadData = useCallback(async () => {
    try {
      setLoading(true)
      const productData = await getProductPerformanceData(dateRange)
      setRawData(productData)
    } catch (error) {
      console.error('Error loading product performance data:', error)
      setRawData({ value: [], mass: [] })
    } finally {
      setLoading(false)
    }
  }, [dateRange])

  useEffect(() => {
    loadData()
  }, [loadData])

  // Exclude the current incomplete week (last entry if it's this week)
  const trimmedData = useMemo(() => {
    const source = (metric === 'mass' ? rawData?.mass : rawData?.value) ?? []
    if (source.length === 0) return source

    // Get current ISO week number
    const now = new Date()
    const tempDate = new Date(now)
    tempDate.setHours(0, 0, 0, 0)
    tempDate.setDate(tempDate.getDate() + 3 - ((tempDate.getDay() + 6) % 7))
    const week1 = new Date(tempDate.getFullYear(), 0, 4)
    const currentWeek = 1 + Math.round(((tempDate - week1) / 86400000 - 3 + ((week1.getDay() + 6) % 7)) / 7)

    const last = source[source.length - 1]
    const match = last.week?.match(/^W(\d+)/)
    if (match && parseInt(match[1], 10) === currentWeek) {
      return source.slice(0, -1)
    }
    return source
  }, [metric, rawData])
  const data = trimmedData

  // Build a mass lookup by week label for tooltip display
  const massLookup = useMemo(() => {
    const lookup = {}
    ;(rawData?.mass ?? []).forEach(item => {
      const entry = { ...item }
      delete entry.week
      delete entry.total
      lookup[item.week] = entry
    })
    return lookup
  }, [rawData.mass])

  // Enrich chart data with cross-references for tooltip
  const enrichedData = useMemo(() => {
    const valueLookup = {}
    rawData.value.forEach(item => { valueLookup[item.week] = item })
    const massLookupLocal = {}
    rawData.mass.forEach(item => { massLookupLocal[item.week] = item })

    return data.map(item => {
      const enriched = { ...item }
      const valRow = valueLookup[item.week] || {}
      const massRow = massLookupLocal[item.week] || {}

      // Attach both value and mass totals
      enriched._valueTotal = valRow.total || 0
      enriched._massTotal = massRow.total || 0

      // Attach per-strain value for tooltip when viewing mass
      Object.keys(item).forEach(key => {
        if (key !== 'week' && key !== 'total') {
          enriched[`_value_${key}`] = valRow[key] || 0
        }
      })
      return enriched
    })
  }, [data, rawData])

  const isLoading = externalLoading !== undefined ? externalLoading : loading
  if (isLoading) {
    return (
      <div className={`reports-chart-container ${compact ? 'compact' : ''}`}>
        {!compact && !externalDateRange && (
          <div className="flex justify-between items-center mb-4">
            <div></div>
            <DateRangeSelector
              value={dateRange}
              onValueChange={setInternalDateRange}
              className="w-[140px]"
            />
          </div>
        )}
        <div className="reports-loading">
          Loading product performance data...
        </div>
      </div>
    )
  }

  if (!data || data.length === 0) {
    return (
      <div className={`reports-chart-container ${compact ? 'compact' : ''}`}>
        {!compact && !externalDateRange && (
          <div className="flex justify-between items-center mb-4">
            <div></div>
            <DateRangeSelector
              value={dateRange}
              onValueChange={setInternalDateRange}
              className="w-[140px]"
            />
          </div>
        )}
        <div className="reports-loading">
          No product performance data available for the selected period
        </div>
      </div>
    )
  }

  // Collect strains from BOTH datasets so colours stay stable across tabs
  const strains = new Set()
  const addStrains = (rows) => rows.forEach(item => {
    Object.keys(item).forEach(key => {
      if (key !== 'week' && key !== 'total' && !key.startsWith('_')) {
        strains.add(key)
      }
    })
  })
  addStrains(rawData.value)
  addStrains(rawData.mass)

  // Assign colours by revenue rank (stable across tabs)
  const revenueTotals = {}
  Array.from(strains).forEach(strain => {
    revenueTotals[strain] = rawData.value.reduce((sum, item) => sum + (item[strain] || 0), 0)
  })
  const strainsByRevenue = Array.from(strains).sort((a, b) => revenueTotals[b] - revenueTotals[a])
  const strainColorMap = Object.fromEntries(
    strainsByRevenue.map((s, i) => [s, chartPalette[i % chartPalette.length]])
  )

  // Sort by most recent week's value — largest at bottom, smallest at top
  const lastWeek = data.length > 0 ? data[data.length - 1] : {}
  const strainList = Array.from(strains).sort((a, b) => (lastWeek[b] || 0) - (lastWeek[a] || 0))

  const yAxisLabel = metric === 'mass' ? 'Mass (kg)' : 'Revenue (£)'

  return (
    <div className={compact ? 'reports-chart-container compact' : ''}>
      <div className="flex justify-between items-center mb-4">
        <DashboardTabs
          tabs={metricTabs}
          value={metric}
          onValueChange={setMetric}
          size="small"
        />
        {!compact && !externalDateRange && (
          <DateRangeSelector
            value={dateRange}
            onValueChange={setInternalDateRange}
            className="w-[140px]"
          />
        )}
      </div>
      <div style={{ width: '100%', height: compact ? 300 : 480 }}>
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart
            data={enrichedData}
            margin={{ top: 10, right: 30, left: 20, bottom: 5 }}
            stackOffset="none"
          >
            <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
            <XAxis
              dataKey="week"
              axisLine={false}
              tickLine={false}
              tick={{
                fontSize: 11,
                fill: 'var(--muted-foreground)',
                fontFamily: 'Geist, -apple-system, BlinkMacSystemFont, sans-serif'
              }}
              angle={-45}
              textAnchor="end"
              height={60}
            />
            <YAxis
              axisLine={false}
              tickLine={false}
              tick={{
                fontSize: 12,
                fill: 'var(--muted-foreground)',
                fontFamily: 'Geist, -apple-system, BlinkMacSystemFont, sans-serif'
              }}
              label={{
                value: yAxisLabel,
                angle: -90,
                position: 'insideLeft',
                style: { textAnchor: 'middle', fontSize: '12px', fill: 'var(--muted-foreground)' }
              }}
            />
            <Tooltip content={<CustomTooltip massLookup={massLookup} />} />
            <Legend
              wrapperStyle={{
                paddingTop: '20px',
                paddingBottom: '40px',
                fontSize: '12px',
                fontFamily: 'Geist, -apple-system, BlinkMacSystemFont, sans-serif'
              }}
            />
            {strainList.map((strain) => (
              <Area
                key={strain}
                type="monotone"
                dataKey={strain}
                stackId="1"
                stroke={strainColorMap[strain]}
                fill={strainColorMap[strain]}
                fillOpacity={0.6}
                strokeWidth={1.5}
                connectNulls
              />
            ))}
          </AreaChart>
        </ResponsiveContainer>
      </div>
    </div>
  )
}
